import { formatUserFacingError } from '@/lib/errors'
import { useState } from 'react'
import { createPaymentIntentFn } from '@/lib/firebase/functions'
import type { CartItem, ShopAddress } from '../types'

interface CheckoutState {
  clientSecret: string | null
  orderId: string | null
  receiptToken: string | null
  loading: boolean
  error: string | null
}

export function useCheckout() {
  const [state, setState] = useState<CheckoutState>({
    clientSecret: null,
    orderId: null,
    receiptToken: null,
    loading: false,
    error: null,
  })

  async function initiateCheckout(items: CartItem[], shipping: ShopAddress) {
    setState({ clientSecret: null, orderId: null, receiptToken: null, loading: true, error: null })
    try {
      const result = await createPaymentIntentFn({
        items: items.map((item) => ({
          productId: item.productId,
          variantId: item.variantId,
          quantity: item.quantity,
        })),
        shipping,
        termsAccepted: true,
      })
      setState({
        clientSecret: result.data.clientSecret,
        orderId: result.data.orderId,
        receiptToken: result.data.receiptToken,
        loading: false,
        error: null,
      })
      return result.data
    } catch (err: unknown) {
      const message = formatUserFacingError(err, 'Checkout failed. Please try again.')
      setState({
        clientSecret: null,
        orderId: null,
        receiptToken: null,
        loading: false,
        error: message,
      })
      throw err
    }
  }

  function reset() {
    setState({ clientSecret: null, orderId: null, receiptToken: null, loading: false, error: null })
  }

  return { ...state, initiateCheckout, reset }
}
